/**
 * Screen recording with voice for the CSP thread composer — the moving-picture
 * sibling of the 🎙 voice note, in the private client chat, the WhatsApp-bridged
 * group channels and ticket threads alike (one Thread component, so one hook).
 * Parity with the client panel (Nikhil 2026-09-14): a rep walking a client
 * through a fix in prose loses the clicks; a recording of the design app with
 * narration keeps them. The recording travels as an ordinary attachment — the
 * same upload() the 📎 uses, the same pending-media chip, sent with the message
 * — so nothing downstream has to know it is a video.
 *
 * The CSP differs from the client panel in what is worth recording. The client
 * reports a bug in the tab the panel lives in, so the panel prefers its own tab.
 * The rep never wants the CSP tab itself: they record the design app open in
 * ANOTHER tab, or the whole screen, while they narrate (Nikhil 2026-09-14). So
 * this side has one entry point, ⏺, and asks Chrome to hide the CSP's own tab
 * from the picker — see other_tab_or_screen_constraints.
 *
 * The microphone is always opened, whatever the share carries. Narration is
 * the point, and MediaRecorder encodes a single audio track: when the share
 * brings tab audio of its own the two are mixed through WebAudio into one
 * track, when it does not the mic is that track. A refused mic is a note, not
 * a failure — a silent recording still shows the clicks.
 *
 * Exercised by screen_record.test.js without a browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Five minutes. WHY: long enough for a narrated walkthrough, short enough that
 * a recording the rep forgot about — the browser's "sharing your screen" bar
 * stays up for as long as the track is live — stops on its own; and the bitrate
 * budget below is sized against it.
 */
export const SCREEN_RECORD_MAX_MS = 5 * 60 * 1000;

/**
 * Budgeted against upload()'s 16 MB attachment cap (CommsThread: `file.size >
 * 16 * 1024 * 1024` → "Attachments are capped at 16MB"): (350k + 48k) bits/s
 * over the full 300 s is ~14.9 MB, so even a maximum-length recording uploads
 * instead of being rejected after the rep already sat through it. Screen
 * content is mostly static, so the codec spends the budget on what moves.
 */
export const VIDEO_BITS_PER_SECOND = 350000;
export const AUDIO_BITS_PER_SECOND = 48000;

/**
 * Best first. Chromium takes vp9, Firefox vp8; the last two are for a browser
 * that records but does not admit to our preferred codec pair.
 */
export const SCREEN_RECORD_MIME_CANDIDATES = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm', 'video/mp4'];

export const RECORD_SCREEN_TITLE = 'Record another tab or your screen, with voice';
export const STOP_SCREEN_TITLE = 'Stop recording — the clip becomes the pending attachment';
export const MIC_REFUSED_NOTE = 'Recording without your microphone — allow it in the browser to narrate.';
export const CAP_REACHED_NOTE = `Recording stopped at the ${SCREEN_RECORD_MAX_MS / 60000}-minute limit — send this one and record another if you need to.`;
export const START_FAILED_NOTE = 'Could not start the screen recording.';

/**
 * getDisplayMedia's dictionary plus two Chrome hints. `audio: true` asks for
 * the tab/system audio, which Chromium attaches only if the rep ticks the box —
 * the microphone is a separate request, made after the share regardless.
 *
 * `selfBrowserSurface: 'exclude'` hides the CSP's own tab from the picker: the
 * rep is choosing among the things worth showing (the design app in another
 * tab, a window, the screen), never the portal they are typing in. The client
 * panel does the opposite (preferCurrentTab + selfBrowserSurface 'include')
 * because the client's bug is in the tab the panel lives in. `surfaceSwitching:
 * 'include'` keeps Chrome's "switch tab" affordance in the share bar so a rep
 * who started on the wrong tab can move without stopping. A browser that does
 * not know the hints drops them and shows its normal picker — same recording,
 * one more click.
 */
export const other_tab_or_screen_constraints = () => ({
    video: true,
    audio: true,
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
});

const media_devices = () => (typeof navigator === 'undefined' ? undefined : navigator.mediaDevices);
const recorder_ctor = () => (typeof window === 'undefined' ? undefined : window.MediaRecorder);
const audio_context_ctor = () => (typeof window === 'undefined' ? undefined : window.AudioContext);
const err_name = (e) => (e && e.name) || undefined;

/** Both halves are needed: iOS Safari has MediaRecorder but no display capture. */
export const screen_recording_supported = () => {
    const md = media_devices();
    return !!md && typeof md.getDisplayMedia === 'function' && typeof recorder_ctor() === 'function';
};

/**
 * Microphone capture + an encoder — the voice note's needs. Kept next to the
 * screen check because they fail together for the same non-obvious reason: a
 * page Chrome does not consider a secure context (a self-signed dev
 * certificate, an http origin that is not localhost) has no `mediaDevices` at
 * all, so a visible 🎙 there could only throw on the first click.
 */
export const voice_note_supported = () => {
    const md = media_devices();
    return !!md && typeof md.getUserMedia === 'function' && typeof recorder_ctor() === 'function';
};

/** The first candidate the browser admits to; undefined → let MediaRecorder pick its own default. */
export const pick_screen_recording_mime = () => {
    const Recorder = recorder_ctor();
    if (!Recorder || typeof Recorder.isTypeSupported !== 'function') return undefined;
    return SCREEN_RECORD_MIME_CANDIDATES.find((t) => Recorder.isTypeSupported(t));
};

/**
 * The bare container type for the File: `video/webm;codecs=vp8,opus` →
 * `video/webm`. MediaRecorder's mimeType carries the codecs suffix; the
 * stored mimetype should not, so the thread's `indexOf('video/') === 0` player
 * check and any server-side type whitelist see a plain video type — the same
 * thing the voice note does with its `audio/webm`.
 */
export const screen_recording_container = (mime) => (mime && String(mime).indexOf('mp4') !== -1 ? 'video/mp4' : 'video/webm');

/** `screen-recording-<ts>.webm`, or .mp4 when that is the container the browser handed back. */
export const screen_recording_file_name = (mime, now = Date.now()) =>
    `screen-recording-${now}.${screen_recording_container(mime) === 'video/mp4' ? 'mp4' : 'webm'}`;

/** m:ss — the live counter on the stop button. */
export const fmt_elapsed = (ms) => {
    const secs = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;
};

/**
 * Tab audio and narration into one track. MediaRecorder encodes the first
 * audio track it finds and ignores the rest, so two tracks side by side would
 * silently drop one; the graph is the only way to keep both.
 */
const mix_audio = (Ctx, a, b) => {
    const context = new Ctx();
    const destination = context.createMediaStreamDestination();
    context.createMediaStreamSource(a).connect(destination);
    context.createMediaStreamSource(b).connect(destination);
    return { context, stream: destination.stream };
};

/**
 * The recorder behind the ⏺ button. Returns
 *   { supported, recording, elapsed_ms, note, clear_note, toggle }
 * `toggle()` starts a recording, or — while one runs — stops it and hands the
 * File to `on_file` (the thread's upload()). `on_file` is read when the
 * recording STOPS, not when it starts: the CSP's Thread instance survives a
 * channel switch (it resets its state rather than remounting), and the clip
 * must land in the thread that is open when the rep presses ■ — the one whose
 * pending-attachment chip they will see.
 */
export const use_screen_recorder = (on_file) => {
    const [supported] = useState(screen_recording_supported);
    const [started_at, set_started_at] = useState(null);
    const [elapsed_ms, set_elapsed_ms] = useState(0);
    const [note, set_note] = useState(null);
    const on_file_ref = useRef(on_file);
    on_file_ref.current = on_file;
    const recorder_ref = useRef(null);
    /** Every stream we opened: the display, the mic, and the mixer's output when there was one. */
    const streams_ref = useRef([]);
    /** The mixing graph, when the share carried audio of its own. */
    const context_ref = useRef(null);
    const cap_ref = useRef(null);
    /** The share picker is up: a second tap must not raise a second one. */
    const starting_ref = useRef(false);
    const recording = started_at !== null;

    useEffect(() => {
        if (!recording) return undefined;
        const t = setInterval(() => set_elapsed_ms(Date.now() - started_at), 500);
        return () => clearInterval(t);
    }, [recording, started_at]);

    const release = useCallback(() => {
        if (cap_ref.current !== null) clearTimeout(cap_ref.current);
        cap_ref.current = null;
        streams_ref.current.forEach((s) => s.getTracks().forEach((t) => t.stop()));
        streams_ref.current = [];
        // An open AudioContext is a running audio thread; nothing needs it once the tracks are gone.
        if (context_ref.current && typeof context_ref.current.close === 'function') {
            try { context_ref.current.close(); } catch (e) { /* already closed */ }
        }
        context_ref.current = null;
        recorder_ref.current = null;
    }, []);

    // The thread unmounts when the rep leaves the client, and a recording
    // cannot outlive the composer it would be attached to. Drop the handlers
    // before the stop so the teardown does not upload into a gone component,
    // then release the tracks — an abandoned display track leaves the browser's
    // "sharing your screen" bar up with nothing behind it.
    useEffect(() => () => {
        const rec = recorder_ref.current;
        if (rec) {
            rec.ondataavailable = null;
            rec.onstop = null;
            if (rec.state !== 'inactive') { try { rec.stop(); } catch (e) { /* already stopped */ } }
        }
        release();
    }, [release]);

    const start = async () => {
        const md = media_devices();
        const Recorder = recorder_ctor();
        if (!md || !Recorder) return;
        set_note(null);

        let display;
        try {
            display = await md.getDisplayMedia(other_tab_or_screen_constraints());
        } catch (e) {
            // Dismissing the picker is a decision, not a failure: back to idle, no note.
            const name = err_name(e);
            if (name !== 'NotAllowedError' && name !== 'AbortError') set_note(START_FAILED_NOTE);
            return;
        }
        streams_ref.current = [display];

        // Asked for AFTER the share, so a dismissed picker never raises a mic
        // prompt for a recording that is not happening.
        let mic = null;
        try {
            mic = await md.getUserMedia({ audio: true });
            streams_ref.current.push(mic);
        } catch (e) {
            // A silent recording still shows the clicks; a refused mic must not throw that away.
            set_note(MIC_REFUSED_NOTE);
        }

        // From here to start(), nothing can fail for a reason the rep could act
        // on, and any failure must not leave the share and the mic open behind
        // a button that says idle.
        let rec;
        let picked;
        try {
            let audio = display.getAudioTracks();
            if (mic) {
                const Ctx = audio_context_ctor();
                if (audio.length && Ctx) {
                    const mixer = mix_audio(Ctx, display, mic);
                    context_ref.current = mixer.context;
                    streams_ref.current.push(mixer.stream);
                    audio = mixer.stream.getAudioTracks();
                } else {
                    // Nothing to mix, or nothing to mix with — narration beats tab noise.
                    audio = mic.getAudioTracks();
                }
            }
            picked = pick_screen_recording_mime();
            rec = new Recorder(new window.MediaStream([...display.getVideoTracks(), ...audio]), {
                ...(picked ? { mimeType: picked } : {}),
                videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
                audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
            });
        } catch (e) {
            release();
            set_note(START_FAILED_NOTE);
            return;
        }
        const chunks = [];
        rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
        rec.onstop = async () => {
            release();
            set_started_at(null);
            set_elapsed_ms(0);
            const type = screen_recording_container(rec.mimeType || picked);
            const blob = new Blob(chunks, { type });
            if (!blob.size) return; // nothing came through (mis-tap, or the share died on arrival)
            const deliver = on_file_ref.current;
            if (deliver) await deliver(new File([blob], screen_recording_file_name(type), { type }));
        };
        // "Stop sharing" in the browser's own bar ends the video track; without
        // this the recorder would run on against a dead track and the rep would
        // have no way back to idle.
        display.getVideoTracks().forEach((t) => t.addEventListener('ended', () => {
            if (rec.state !== 'inactive') rec.stop();
        }, { once: true }));

        try {
            rec.start();
        } catch (e) {
            release();
            set_note(START_FAILED_NOTE);
            return;
        }
        recorder_ref.current = rec;
        cap_ref.current = setTimeout(() => {
            if (rec.state === 'inactive') return;
            set_note(CAP_REACHED_NOTE);
            rec.stop();
        }, SCREEN_RECORD_MAX_MS);
        set_started_at(Date.now());
        set_elapsed_ms(0);
    };

    const toggle = async () => {
        const active = recorder_ref.current;
        if (active) {
            if (active.state !== 'inactive') active.stop(); // onstop does the upload and the cleanup
            return;
        }
        if (starting_ref.current) return;
        starting_ref.current = true;
        try { await start(); } finally { starting_ref.current = false; }
    };

    return { supported, recording, elapsed_ms, note, clear_note: () => set_note(null), toggle };
};
